import React, { useState } from 'react'
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, TextField, Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { helperProps, compactPlaceholderSx } from './common'

const LOSS_REASONS = ['Commercials too high', 'Lost to competitor', 'Client dropped the requirement', 'Serviceability gap', 'Other']

/**
 * ReturnDeclineDialog — inline form for Return / Escalate / Decline actions.
 * variant: 'return-sl' | 'return-l1' | 'escalate' | 'decline'
 */
export default function ReturnDeclineDialog({ open, onClose, variant = 'return-sl', onSubmit, submitting = false }) {
  const { enqueueSnackbar } = useSnackbar()
  const [remarks, setRemarks] = useState('')
  const [rfi, setRfi] = useState(false)
  const [reason, setReason] = useState('')
  const [showErr, setShowErr] = useState(false) // reveal inline field errors after a blocked submit attempt

  const cfg = {
    'return-sl': { title: 'Return to Sales Leader', cta: 'Return to Sales Leader', needRemarks: true, showRfi: true },
    'return-l1': { title: 'Return to L1 Reviewer', cta: 'Return to L1', needRemarks: true, showRfi: false },
    escalate: { title: 'Escalate to Functional Head', cta: 'Escalate', needRemarks: false, showRfi: false },
    decline: { title: 'Decline — close as unviable', cta: 'Decline deal', needRemarks: true, showReason: true },
  }[variant]

  const blocked = (cfg.needRemarks && !remarks.trim()) || (cfg.showReason && !reason)

  const submit = () => {
    if (blocked) { setShowErr(true); return }
    if (submitting) return
    setShowErr(false)
    if (onSubmit) {
      // Parent performs the API call (return / escalate / decline) and closes on success.
      onSubmit({ remarks: remarks.trim(), brandRfiRequired: rfi, reason })
      return
    }
    enqueueSnackbar(`${cfg.title} — recorded with actor + timestamp.`, { variant: variant === 'decline' ? 'warning' : 'info' })
    setRemarks(''); setRfi(false); setReason('')
    onClose && onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={compactPlaceholderSx}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: 'primary.dark' }}>{cfg.title}</DialogTitle>
      <DialogContent dividers>
        {variant === 'escalate' && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
            Escalate without changes — use when something looks off, or during a "review every working" period. A note is optional.
          </Typography>
        )}
        {cfg.showReason && (
          <TextField
            select fullWidth size="small" required label="Loss reason" value={reason} onChange={(e) => setReason(e.target.value)}
            error={showErr && !reason}
            {...helperProps(showErr && !reason ? 'Loss reason is required.' : undefined)}
            sx={{ mb: 1.5 }}
          >
            {LOSS_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
        )}
        <TextField
          fullWidth multiline minRows={3} size="small"
          required={cfg.needRemarks}
          label={cfg.needRemarks ? 'Remarks (mandatory)' : 'Note (optional)'}
          value={remarks} onChange={(e) => setRemarks(e.target.value)}
          error={showErr && cfg.needRemarks && !remarks.trim()}
          {...helperProps(showErr && cfg.needRemarks && !remarks.trim() ? 'Remarks are required.' : undefined)}
          placeholder="Type your remarks for the sales leader / pricing team…"
        />
        {cfg.showRfi && (
          <Box sx={{ mt: 1, border: '1px dashed', borderColor: 'divider', borderRadius: '8px', px: 1.5, py: 0.5, bgcolor: '#FAFAFC' }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={rfi} onChange={(e) => setRfi(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 12, fontWeight: 600 }}>Brand-level RFI data required from the sales team</Typography>}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" color={variant === 'decline' ? 'error' : 'primary'} disabled={submitting} onClick={submit} sx={{ textTransform: 'none' }}>
          {submitting ? 'Working…' : cfg.cta}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
